import React, { useEffect, useRef, useState } from 'react';

import clsx from 'clsx';
import { TopBarDropdown } from './TopBarDropdown';
import { TopBarButtons } from './TopBarButtons';
import * as H from 'history';

import { Media } from '../../utils/breakpoints-helper';

import './index.css';

export interface LinkProps<S = H.LocationState>
  extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  component?: React.ComponentType | undefined;
  to:
    | H.LocationDescriptor<S>
    | ((location: H.Location<S>) => H.LocationDescriptor<S>);
  replace?: boolean | undefined;
  innerRef?: React.Ref<HTMLAnchorElement> | undefined;
}

export interface MktTopBarItem {
  id: number;
  label: string;
  url: string;
}

export enum MktTopBarVariantsTypes {
  light = 'light',
  dark = 'dark',
  transparent = 'transparent',
}

export type MktTopbarTransparentDesktopType = 'light' | 'dark';

export type MktTopBarVariants = `${MktTopBarVariantsTypes}`;

export interface MktTopBarNestableItem extends MktTopBarItem {
  subLinks?: MktTopBarItem[];
}

export interface MktTopBarProps {
  homeItem: MktTopBarItem;
  menuItems?: MktTopBarNestableItem[];
  loginItem?: MktTopBarItem;
  signUpItem?: MktTopBarItem;
  linkComponent: React.FC<LinkProps>;
  variant: `${MktTopBarVariants}`;
  transparentVariant: MktTopbarTransparentDesktopType;
  fixedVariant: `${MktTopBarVariants}` | null;
  positiveLogo: string;
  negativeLogo: string;
}

export const MktTopBar: React.FC<MktTopBarProps> = ({
  homeItem,
  menuItems,
  loginItem,
  signUpItem,
  linkComponent,
  variant = 'dark',
  transparentVariant = 'dark',
  fixedVariant = null,
  positiveLogo,
  negativeLogo,
}) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
  const [currentVariant, setCurrentVariant] =
    useState<MktTopBarVariants>('transparent');

  const [scrollY, setScrollY] = useState(0);
  const isDesktop = Media().lg();
  const isLight = currentVariant === MktTopBarVariantsTypes.light;

  // if variant = transparent and scroll > 80px will change the background color
  const scrollThreshold = 80;

  const trigger = useRef<HTMLDivElement>(null);

  // close on click outside
  useEffect(() => {
    const clickHandler = ({ target }: MouseEvent) => {
      if (trigger.current?.contains(target as HTMLElement)) return;
      setActiveDropdown(null);
      setIsExpanded(false);
    };
    document.addEventListener('click', clickHandler);
    return () => document.removeEventListener('click', clickHandler);
  });

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [variant]);

  // change topbar color when scroll y is bigger than the scrollThreshold
  useEffect(() => {
    if (scrollY >= scrollThreshold && !fixedVariant) {
      setCurrentVariant(variant);
    } else {
      !fixedVariant && setCurrentVariant('transparent');
    }
  }, [scrollY, scrollThreshold]);

  useEffect(() => {
    if (fixedVariant) {
      setCurrentVariant(fixedVariant);
    } else {
      setCurrentVariant('transparent');
    }
  }, [fixedVariant]);

  const expandHandler = (): void => {
    if (activeDropdown) setActiveDropdown(null);
    setIsExpanded(!isExpanded);
  };

  const dropdownHandler = (id: number): void => {
    if (id && activeDropdown !== id) {
      return setActiveDropdown(id);
    } else if (id === activeDropdown) {
      return setActiveDropdown(null);
    }
  };

  const Link = linkComponent;

  const logo = () => {
    if (
      currentVariant === MktTopBarVariantsTypes.transparent &&
      transparentVariant === 'dark' &&
      !isExpanded
    ) {
      return positiveLogo;
    } else if (
      currentVariant === MktTopBarVariantsTypes.transparent &&
      transparentVariant === 'light' &&
      !isExpanded
    ) {
      return negativeLogo;
    } else if (
      (currentVariant === MktTopBarVariantsTypes.light &&
        scrollY > scrollThreshold) ||
      fixedVariant === MktTopBarVariantsTypes.light
    ) {
      return positiveLogo;
    } else if (
      (currentVariant === MktTopBarVariantsTypes.dark &&
        scrollY > scrollThreshold) ||
      fixedVariant === MktTopBarVariantsTypes.dark
    ) {
      return negativeLogo;
    } else {
      return positiveLogo;
    }
  };

  return (
    <nav
      ref={trigger}
      className={clsx(
        `wh-min-h-[88px] wh-w-screen wh-sticky wh-top-0 lg:wh-flex lg:wh-justify-between lg:wh-px-7 wh-transition-all`,
        { 'wh-bg-blue-dark': currentVariant === MktTopBarVariantsTypes.dark },
        {
          'wh-bg-white-off': currentVariant === MktTopBarVariantsTypes.light,
        },
        {
          'wh-bg-[transparent]':
            currentVariant === MktTopBarVariantsTypes.transparent &&
            !isExpanded &&
            !fixedVariant,
        },
        {
          'wh-bg-white':
            isExpanded && scrollY < scrollThreshold && !fixedVariant,
        },
      )}
    >
      <div
        className={clsx(
          'wh-flex wh-h-[88px] wh-w-full wh-items-center wh-p-4 lg:wh-h-auto lg:wh-w-auto lg:wh-border-none lg:wh-p-0',
          `${menuItems ? 'wh-justify-between' : 'wh-justify-center'}`,
        )}
      >
        {menuItems && (
          <button
            className={clsx('lg:wh-hidden', { 'wh-hidden': isExpanded })}
            type="button"
            onClick={expandHandler}
          >
            <i
              className={clsx(
                `wh-icon ${
                  isExpanded
                    ? ''
                    : 'wh-icon-hamburger-menu wh-translate-y-[4px] wh-text-[24px]'
                } wh-cursor-pointer ${
                  isLight ||
                  currentVariant === MktTopBarVariantsTypes.transparent
                    ? 'wh-text-blue-dark'
                    : 'wh-text-white'
                } `,
                {
                  'wh-text-white':
                    transparentVariant === 'light' &&
                    !isExpanded &&
                    scrollY < scrollThreshold &&
                    !fixedVariant,
                },
                {
                  'wh-text-blue-dark':
                    transparentVariant === 'dark' &&
                    !isExpanded &&
                    scrollY < scrollThreshold &&
                    !fixedVariant,
                },
              )}
            />
          </button>
        )}
        <Link to={homeItem.url}>
          <img
            className="lg:wh-block wh-max-w-[207px] wh-h-[40px]"
            src={logo()}
            alt={homeItem.label}
          />
        </Link>
        <button
          className="lg:wh-hidden"
          type="button"
          onClick={() => (isExpanded ? expandHandler() : null)}
        >
          <i
            className={clsx(
              { 'wh-icon-exit wh-text-[24px]': isExpanded },
              ` wh-text-[24px] wh-translate-y-[4px]
                wh-cursor-pointer  ${
                  isLight ||
                  currentVariant === MktTopBarVariantsTypes.transparent
                    ? 'wh-text-blue-dark'
                    : 'wh-text-white'
                } 
                `,
              {
                'wh-text-white':
                  transparentVariant === 'light' &&
                  !isExpanded &&
                  scrollY < scrollThreshold &&
                  !fixedVariant,
              },
              {
                'wh-text-blue-dark':
                  transparentVariant === 'dark' &&
                  !isExpanded &&
                  scrollY < scrollThreshold &&
                  !fixedVariant,
              },
            )}
          />
        </button>
      </div>
      <div
        className="wh-group wh-overflow-hidden lg:wh-overflow-visible"
        aria-expanded={isExpanded}
      >
        <div className="wh-flex wh-h-0 wh-flex-col wh-gap-5 group-aria-expanded:wh-h-auto group-aria-expanded:wh-px-3 group-aria-expanded:wh-pb-7 group-aria-expanded:wh-pt-5 lg:wh-h-full lg:wh-w-auto lg:wh-flex-row lg:wh-items-center lg:wh-gap-2 lg:wh-p-0">
          <ul className="wh-flex wh-flex-col wh-gap-3 lg:wh-h-full lg:wh-flex-row lg:wh-items-center lg:wh-gap-x-[30px]">
            {menuItems?.map((item: MktTopBarNestableItem) => {
              if (item.subLinks) {
                return (
                  <li
                    key={item.id}
                    className="wh-relative wh-flex wh-flex-col lg:wh-h-full lg:wh-flex-row"
                    onMouseEnter={() => dropdownHandler(item.id)}
                    onMouseLeave={() => dropdownHandler(item.id)}
                  >
                    <div
                      className={clsx(
                        'wh-flex wh-cursor-pointer wh-items-center wh-gap-1',
                        `${
                          activeDropdown === item.id
                            ? isLight
                              ? 'lg:wh-bg-white lg:[&>p]:wh-text-gold lg:[&>span>i]:wh-text-gold'
                              : 'lg:wh-bg-blue-dark  lg:[&>p]:wh-text-gold lg:[&>span>i]:wh-text-gold'
                            : ' lg:[&>p]:hover:wh-text-gold  lg:[&>span>i]:hover:wh-text-gold'
                        }`,
                        {
                          'wh-delay-[100ms]': !activeDropdown,
                        },
                        {
                          'lg:wh-bg-blue-dark  lg:[&>p]:wh-text-gold lg:[&>span>i]:wh-text-gold':
                            activeDropdown === item.id &&
                            currentVariant ===
                              MktTopBarVariantsTypes.transparent,
                        },
                      )}
                    >
                      <p
                        className={clsx(
                          `wh-text-lg wh-font-medium lg:wh-px-[13px] ${
                            isLight ||
                            currentVariant ===
                              MktTopBarVariantsTypes.transparent
                              ? 'wh-text-blue-dark'
                              : ' wh-text-white'
                          } lg:wh-text-base`,
                          {
                            'wh-text-white':
                              transparentVariant === 'light' &&
                              isDesktop &&
                              scrollY < scrollThreshold &&
                              !fixedVariant,
                          },
                          {
                            'wh-text-blue-dark':
                              transparentVariant === 'dark' &&
                              isDesktop &&
                              scrollY < scrollThreshold &&
                              !fixedVariant,
                          },
                        )}
                      >
                        {item.label}
                      </p>
                      {!isDesktop && (
                        <span
                          className={`wh-flex wh-h-3 wh-w-3 wh-items-center wh-justify-center wh-rounded-full ${
                            isLight ||
                            currentVariant !==
                              MktTopBarVariantsTypes.transparent
                              ? 'wh-bg-[#C3885A80]'
                              : 'wh-bg-[#EDDDD480]'
                          } `}
                        >
                          <i className="wh-icon wh-icon-arrow-right icon-arrow-right wh-text-white" />
                        </span>
                      )}
                    </div>
                    <div ref={trigger}>
                      <TopBarDropdown
                        isActive={activeDropdown === item.id}
                        items={item}
                        linkComponent={Link}
                        variant={currentVariant}
                        onClick={() => {
                          if (!isDesktop) {
                            dropdownHandler(item.id);
                            return setIsExpanded(false);
                          }
                        }}
                      />
                    </div>
                  </li>
                );
              }
              return (
                <li
                  key={item.id}
                  className="wh-flex wh-items-center wh-gap-1 lg:wh-w-full lg:wh-h-full lg:wh-whitespace-nowrap"
                >
                  <Link
                    to={item.url}
                    className={clsx(
                      `wh-text-lg wh-font-medium lg:wh-px-[13px] lg:wh-w-full lg:wh-h-full lg:wh-flex lg:wh-justify-center lg:wh-items-center ${
                        isLight ||
                        currentVariant === MktTopBarVariantsTypes.transparent
                          ? 'wh-text-blue-dark'
                          : 'wh-text-white'
                      } lg:wh-text-base lg:hover:wh-text-gold`,
                      {
                        'wh-text-white':
                          transparentVariant === 'light' &&
                          isDesktop &&
                          scrollY < scrollThreshold &&
                          !fixedVariant,
                      },
                      {
                        'wh-text-blue-dark':
                          transparentVariant === 'dark' &&
                          isDesktop &&
                          scrollY < scrollThreshold &&
                          !fixedVariant,
                      },
                    )}
                    onClick={() => setIsExpanded(false)}
                  >
                    {item.label}
                  </Link>
                </li>
              );
            })}
          </ul>
          {(loginItem || signUpItem) && (
            <div className="wh-flex wh-h-auto lg:wh-h-full wh-items-center lg:wh-ml-[30px]">
              <TopBarButtons
                loginItem={loginItem}
                signUpItem={signUpItem}
                linkComponent={Link}
                variant={currentVariant}
                transparentVariant={transparentVariant}
                isDesktop={isDesktop}
                scrollY={scrollY}
                scrollThreshold={scrollThreshold}
                fixedVariant={fixedVariant}
              />
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};
